/* eslint-disable react-refresh/only-export-components */
import { CustomAxiosResponse } from '@/configurations/AxiosConfig'
import RolesService from '@/services/RolesService'
import UsersService from '@/services/UsersService'
import { IProfileUser, IRoles, IUser } from '@/types/type'
import Helpers from '@/utilities/Helpers'
import { createContext, ReactNode, useContext, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useAuth } from '../auth/AuthContext'
import { useAbility } from '../auth/AbilityContext'

export interface IUserContext {
  // allUsers: IUser[]
  getAllUsers: () => Promise<IUser[]>
  allRoles: IRoles[]
  getAllRoles: () => Promise<IRoles[]> | undefined
  createUser: (data: FormData, rawData: any) => Promise<{response: IUser | null, formData: FormData} | null>
  deleteUser: (id: number | string) => Promise<boolean>
  changePassword: (data: { newPassword: string; currentPassword: string }) => Promise<any>
  updateUser: (id: number, data: FormData) => Promise<IUser | IProfileUser | null>
  getUsersByRoleId: (id: number | string) => Promise<IUser[]>
  getUsersByCompanyId: (id: number) => Promise<IUser[]>
  getProfileUser: () => Promise<IProfileUser | null>
  getUserById: (id: number) => Promise<IUser | null>
  createPassword: (data: { token: string; newPassword: string }) => Promise<any>
  uploadUserProfilePhoto: (data: FormData) => Promise<any>
}

const UserContext = createContext<IUserContext | null>(null)

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { user, setUser } = useAuth()
  const { updatePermissions } = useAbility();

  // const [allUsers, setAllUsers] = useState<IUser[]>([])
  const [allRoles, setAllRoles] = useState([])


  const getAllUsers = async (): Promise<IUser[]> => {
    const response: CustomAxiosResponse<IUser[]> = await UsersService.getAllUsers()
    if (Helpers.isResponseSuccessful(response)) {
      // setAllUsers(response.data)
      return response.data
    } else {
      // setAllUsers([])
      return []
    }
  }

  const getUserById = async (id: number): Promise<IUser | null> => {
    const response: CustomAxiosResponse<IUser> = await UsersService.getUserById(id)
    if (Helpers.isResponseSuccessful(response)) {
      // setAllUsers(response.data)
      return response.data
    } else {
      // setAllUsers([])
      return null
    }
  }

  const getUsersByRoleId = async (id: number | string): Promise<IUser[]> => {
    const response: CustomAxiosResponse<IUser[]> = await UsersService.getUsersByRoleId(id)
    if (Helpers.isResponseSuccessful(response)) {
      return response.data
    } else {
      return []
    }
  }

  const getUsersByCompanyId = async (id: number): Promise<IUser[]> => {
    const response: CustomAxiosResponse<IUser[]> = await UsersService.getUsersByCompanyId(id)
    if (Helpers.isResponseSuccessful(response)) {
      return response.data
    } else {
      return []
    }
  }

  const getProfileUser = async (): Promise<IProfileUser | null> => {
    if (user?.id === undefined || user?.id === null) return null
    const response: CustomAxiosResponse<IProfileUser> = await UsersService.getUserById(user.id)
    if (Helpers.isResponseSuccessful(response)) {
      setUser(response.data)
      updatePermissions(response.data.permissions as any);
      return response.data
    } else {
      setUser(null)
      return null
    }
  }

  const getAllRoles = async () => {
    const response = await RolesService.getAllRoles()
    if (Helpers.isResponseSuccessful(response)) {
      setAllRoles(response.data)
      return response.data
    } else {
      setAllRoles([])
      return []
    }
  }

  const createUser = async (data: FormData, rawData: any): Promise<{response: IUser | null, formData: FormData} | null> => {
    const response: CustomAxiosResponse<IUser> = await UsersService.createUser(data);
    if (Helpers.isResponseSuccessful(response)) {
      toast.success(response?.message ?? "Kullanıcı başarıyla oluşturuldu!");
      return { response: response.data, formData: rawData };
    } else {
      toast.error(response?.message ?? "Bir hata oluştu!");
      return null;
    }
  }

  const deleteUser = async (id: number | string): Promise<boolean> => {
    const response: CustomAxiosResponse = await UsersService.deleteUser(id);
    if (Helpers.isResponseSuccessful(response)) {
      toast.success(response?.message ?? "Kullanıcı başarıyla silindi!");
      return true;
    } else {
      toast.error(response?.message ?? "Bir hata oluştu!");
      return false;
    }
  }

  const createPassword = async (data: { token: string; newPassword: string }): Promise<any> => {
    const response: CustomAxiosResponse<any> = await UsersService.createPassword(data);
    if (Helpers.isResponseSuccessful(response)) {
      toast.success(response?.message ?? "Şifre başarıyla oluşturuldu!");
      return true;
    } else {
      toast.error(response?.message ?? "Bir hata oluştu!");
      return null;
    }
  }

  const changePassword = async (data: { newPassword: string; currentPassword: string }) => {
    const response: CustomAxiosResponse = await UsersService.changePassword(data)
    if (Helpers.isResponseSuccessful(response)) {
      toast.success(response?.message ?? "Şifre başarıyla değiştirildi!")
      return response.data
    } else {
      toast.error(response?.message ?? "Bir hata oluştu!")
      return null
    }
  }

  const updateUser = async (id: number, data: FormData) => {
    const response: CustomAxiosResponse<IUser | IProfileUser> = await UsersService.updateUser(id, data)
    if (Helpers.isResponseSuccessful(response)) {
      toast.success(response?.message ?? "Kullanıcı başarıyla güncellendi!")
      if (id === user?.id) {
        // getProfileUser()
        setUser(response.data as IProfileUser)
        updatePermissions(response.data.permissions as IProfileUser['permissions'] as any);
      }
      return response.data
    } else {
      toast.error(response?.message ?? "Bir hata oluştu!")
      return null
    }
  }

  const uploadUserProfilePhoto = async (data: FormData) => {
    const response: CustomAxiosResponse<any> = await UsersService.profilePhoto(data);
    if (Helpers.isResponseSuccessful(response)) {
      response.message && toast.success(response.message);
      return response.data;
    } else {
      toast.error(response?.message ?? "Bir hata oluştu!");
      return null;
    }
  }

  const value = useMemo(
    () => ({
      // allUsers,
      getAllUsers,
      allRoles,
      getAllRoles,
      updateUser,
      createUser,
      deleteUser,
      changePassword,
      getUsersByRoleId,
      getProfileUser,
      getUserById,
      createPassword,
      getUsersByCompanyId,
      uploadUserProfilePhoto
    }), [user])
  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUser must be used within an UserProvider')
  }
  return context
}